/**
 * Aggregate the Scheduled Scans table into the four numbers worth reading
 * before any row.
 *
 * Pure and side-effect free, like its sibling `scheduleRowCopy.ts`, so every
 * branch is testable without mounting a table.
 *
 * WHY THESE FOUR, and why none of them counts schedules:
 *
 * The table already answers "what did I set up" -- five of its seven columns
 * are configuration. What it cannot answer without reading every row is "what
 * changed, and is anything wrong". A band reading "65 schedules, 57 active"
 * would restate the configuration and give nobody a reason to act, which
 * matters because the measured failure here is conversion, not awareness: on
 * prod 2026-08-17, 57 running schedules had produced 12 issues in their entire
 * history (tickets.source='rescan') against 189 from team review.
 *
 * So the band carries the state (`findings`), the change (`newFindings`), the
 * scope (`watched`), and the faults (`attention`).
 */
import type { ScheduledScanRow } from '../../lib/api/scheduledScans';
import { isRunOverdue } from './scheduleRowCopy';

export interface ScheduleStats {
  /** Pages actually being watched right now. Paused rows are not. */
  watched: number;
  /**
   * Open findings across every row, paused included.
   *
   * NULL until at least one page has actually been scanned. "0 findings" above
   * a table of never-scanned pages is a clean bill of health nobody checked --
   * the same lie as reporting "0 new" for a baseline run, and it must obey the
   * same rule. Caught by looking at the rendered band, not by a test: the live
   * run showed "OPEN FINDINGS 0" for a schedule created seconds earlier.
   */
  findings: number | null;
  /**
   * New findings since each row's previous run, summed.
   *
   * NULL when no row has ever been compared, which is a different fact from
   * zero and must not be rendered as one.
   */
  newFindings: number | null;
  /**
   * Findings that DISAPPEARED since each row's previous run, summed.
   *
   * The only positive signal this feature can produce. The server has computed
   * it since the feature shipped and, until 2026-08-17, nothing on any surface
   * read it: the type declared it and no component referenced it. A weekly scan
   * that reports only what broke and never what you repaired has no reward in
   * its loop, which is the half of a habit the Hook model says does the work.
   *
   * NULL on the same rule as newFindings: a baseline run repaired nothing, it
   * simply has nothing to compare against.
   */
  fixed: number | null;
  /** Rows that are failing rather than merely idle. */
  attention: number;
}

/**
 * Is this row a FAULT, as opposed to something the user chose?
 *
 * A refused review needs attention, as does an overdue `nextRunAt` after
 * the scheduler has
 * silently skipped the row -- the 41-day overlayqa.com outage, which rendered
 * "Active / within the hour" the whole time (scheduleRowCopy.ts records it).
 *
 * A row the user PAUSED is excluded on purpose. Folding a deliberate pause
 * into the fault count is how a real fault stops standing out, which is the
 * failure this number exists to prevent.
 *
 * Overdue-ness is imported from scheduleRowCopy rather than re-derived here.
 * That module documents `latenessMs` as THE single definition and already has
 * two readers; a third copy would drift the first time the grace window is
 * tuned, and the visible symptom would be a band claiming a fault on a row
 * whose own cell reads "in 7 days".
 */
function needsAttention(schedule: ScheduledScanRow, now: Date): boolean {
  if (['paused_unreachable', 'paused_bot_protection', 'paused_page_target', 'paused_snapshot_limit'].includes(schedule.status)) return true;
  if (schedule.status === 'paused') return false;
  return isRunOverdue(schedule.nextRunAt, now);
}

export function summarizeSchedules(
  schedules: readonly ScheduledScanRow[],
  now: Date,
): ScheduleStats {
  let watched = 0;
  let findingsSum = 0;
  let anyScanned = false;
  let attention = 0;
  let deltaSum = 0;
  let fixedSum = 0;
  let anyCompared = false;

  for (const schedule of schedules) {
    if (schedule.status === 'active') watched += 1;
    findingsSum += schedule.lastFindingCount;
    if (schedule.lastScannedAt !== null) anyScanned = true;
    if (needsAttention(schedule, now)) attention += 1;
    // A baseline run (lastDeltaAdded === null) has nothing to compare against,
    // so it contributes no delta AND does not make the sum meaningful.
    if (schedule.lastDeltaAdded !== null) {
      deltaSum += schedule.lastDeltaAdded;
      anyCompared = true;
    }
    // Keyed on its own null, not on lastDeltaAdded: the two travel together
    // today, and reading one through the other would break silently if that
    // ever stopped being true.
    if (schedule.lastDeltaFixed !== null) fixedSum += schedule.lastDeltaFixed;
  }

  return {
    watched,
    findings: anyScanned ? findingsSum : null,
    newFindings: anyCompared ? deltaSum : null,
    fixed: anyCompared ? fixedSum : null,
    attention,
  };
}
